import React from 'react';
import { marked } from 'marked';
import ChatGraph from '../ChatGraph';
import { formatDateTimeString } from '../utils/commonUtils';
import AnalysisBarGraph from '../graph/AnalysisBarGraph';
import AnalysisLineGraph from '../graph/AnalysisLineGraph';
import AnalysisScatterGraph from '../graph/AnalysisScatterGraph';
import AnalysisMarketLineGraph from '../graph/AnalysisMarketLineGraph';
import Loading from '../Loading';
import { isAuthenticated } from '../api/api';
const marketAccounts = ['stock_price', 'volume', 'market_value', 'per', 'pbr', 'psr', 'dividend_yield'];


const InsightNoteContents = ({ note, noteTitleRef, noteContentsRef }) => {
    if (!isAuthenticated()) {
        return (
            <div className='h-full flex justify-center items-center relative'>
                <div className='absolute top-[100px] py-4 px-[30px] rounded-md bg-gray-600 text-white opacity-[0.9]'>
                    この機能はログインが必要です。
                </div>
            </div>
        )
    }
    if (!note) {
        return <div></div>;
    }

    const getFigData = (chatId, chatHistoryId, figId, figures) => {
        const fig = figures.find(figData =>
            figData.chat_id === chatId &&
            figData.chat_history_id === chatHistoryId &&
            figData.fig_id === figId
        );
        return fig ? fig.fig : null;
    };

    
    const processContent = (noteContents, figures) => {
        const sections = [];
        // [fig]タグの正規表現にdata=の部分もキャプチャ
        const figRegex = /\[fig\s+chat_id=(.*?)\s+chat_history_id=(.*?)\s+fig_id=(.*?)\s+([\s\S]*?)\]/g;

        let lastIndex = 0;
        let match;
    
        // 正規表現で[fig]を探して処理
        while ((match = figRegex.exec(noteContents)) !== null) {
            const chatId = match[1];
            const chatHistoryId = match[2];
            const figId = match[3];
            // マッチする部分の前のテキストを追加
            let beforeText = noteContents.slice(lastIndex, match.index);
    
            // [user], [agent], [evidences], [evidence] などのタグをHTMLに置き換える
            beforeText = beforeText
                .replace(/\[user\](.*?)\[\/user\]/gs, '<div class="query">$1</div>')
                .replace(/\[agent\](.*?)\[\/agent\]/gs, '<div class="answer">$1</div>')
                .replace(/\[evidences\]([\s\S]*?)\[\/evidences\]/g, '<div class="doc-info-container">$1</div>')
                .replace(/\[evidence\]company_name='(.*?)' title='(.*?)' path='(.*?)'\[\/evidence\]/g, '<div class="doc-info"><a target="_blank" href="$3">[$1 $2]</a></div>');
    
            beforeText = beforeText.replace(/\[agent\]/gs, '<div class="answer">');
    
            // テキスト部分を追加
            if (beforeText) {
                sections.push(
                    <div key={`text-${lastIndex}`} dangerouslySetInnerHTML={{ __html: marked(beforeText) }} />
                );
            }
    
            // グラフコンポーネントを追加
            // chat_idが0の場合、data=の部分を使用
            if (chatId === '0') {
                let graphData;
                
                const dataIndex = noteContents.indexOf('data=');
                if (dataIndex !== -1) {
                    // "data=" から始まる部分を抽出
                    const endIndex = noteContents.indexOf('}]}]', dataIndex + 5) + 3; // dataIndex + 5以降の最初の "]" を探す
                    const dataPart = noteContents.slice(dataIndex + 5, endIndex); // "]" までを取得
                    lastIndex = endIndex + 1;

                    // JSONパースする
                    try {
                        graphData = JSON.parse(dataPart); // パースする
                    } catch (error) {
                        console.error("JSON parsing error:", error);
                    }
                }
                
                // graphTypeに応じて適切なグラフコンポーネントを追加
                if (marketAccounts.includes(graphData.col)) {
                    const { companies, name, ...data } = graphData; // companies以外をdataに
                    sections.push(
                        <>
                            <h2 className='text-lg font-bold mb-2'>{name}</h2>
                            <AnalysisMarketLineGraph
                                key={`graph-${match.index}`}
                                data={data}
                                companies={companies}
                                showSaveNotebookIcon={false} 
                            />
                        </>
                    );
                } else if (graphData.graphType === 'bar') {
                    const { companies, name, ...data } = graphData; // companies以外をdataに
                    sections.push(
                        <>
                            <h2 className='text-lg font-bold mb-2'>{name}</h2>
                            <AnalysisBarGraph key={`graph-${match.index}`} data={data} companies={companies} showSaveNotebookIcon={false} showTwoColumnSwitch={false} />
                        </>
                    );
                } else if (graphData.graphType === 'line') {
                    const { companies, name, ...data } = graphData; // companies以外をdataに
                    sections.push(
                        <>
                            <h2 className='text-lg font-bold mb-2'>{name}</h2>
                            <AnalysisLineGraph key={`graph-${match.index}`} data={data} companies={companies} showSaveNotebookIcon={false} showTwoColumnSwitch={false} />
                        </>
                    );
                } else if (graphData.graphType === 'scatter') {
                    sections.push(
                        <>
                        <h2 className='text-lg font-bold mb-2'>{graphData.name[0]}と{graphData.name[1]}の散布図</h2>
                        <AnalysisScatterGraph key={`graph-${match.index}`} data={graphData} showSaveNotebookIcon={false} showTwoColumnSwitch={false} />
                        </>
                    );
                }
            } else {
                // chat_idが0でない場合はfigDataを使う
                const figData = getFigData(chatId, chatHistoryId, figId, figures);
                if (figData) {
                    sections.push(<ChatGraph key={`fig-${figId}`} graphs={[{ fig: figData }]} />);
                }
                lastIndex = figRegex.lastIndex;
            }
    
            
        }
    
        // 残りのテキストを追加
        let afterText = noteContents.slice(lastIndex);
        afterText = afterText
            .replace(/\[user\](.*?)\[\/user\]/gs, '<div class="query">$1</div>')
            .replace(/\[agent\](.*?)\[\/agent\]/gs, '<div class="answer">$1</div>')
            .replace(/\[evidences\]([\s\S]*?)\[\/evidences\]/g, '<div class="doc-info-container">$1</div>')
            .replace(
                /\[evidence\s+company_name=['"](.*?)['"]\s+title=['"](.*?)['"]\s+path=['"](.*?)['"]\]/g,
                '<div class="doc-info"><a target="_blank" href="$3">[$1 $2]</a></div>'
            )
            .replace(/\[\/agent\]/gs, '</div>');
    
        // テキスト部分を追加
        if (afterText) {
            sections.push(<div key={`text-${lastIndex}`} dangerouslySetInnerHTML={{ __html: marked(afterText) }} />);
        }
    
        return sections;
    };
    
    

    const editNoteContents = (note) => {
        const markdownRegex = /\[markdown\](.*?)\[\/markdown\]/gs;
        const sections = [];
        let lastIndex = 0;
        
        // [markdown]タグでテキストを分割
        const matches = [...note.note.contents.matchAll(markdownRegex)];
    
        // マークダウンブロックが存在する場合
        if (matches.length > 0) {
            matches.forEach((match, index) => {
                const markdownStartIndex = match.index;
                const markdownContents = match[1];
                
                // マークダウンの前にある非マークダウン部分を追加
                const nonMarkdownText = note.note.contents.slice(lastIndex, markdownStartIndex);
                if (nonMarkdownText.trim()) {
                    sections.push(
                        <div className="content" key={`non-md-${index}`}>
                            {processContent(nonMarkdownText, note.figures)}
                        </div>
                    );
                }
    
                // マークダウン部分を追加
                sections.push(
                    <div className="content" key={`md-${index}`}>
                        {processContent(markdownContents, note.figures)}
                    </div>
                );
    
                // 次の探索開始位置を更新
                lastIndex = markdownStartIndex + match[0].length;
            });
    
            // 最後のマークダウン後のテキストを追加
            const remainingText = note.note.contents.slice(lastIndex);
            if (remainingText.trim()) {
                sections.push(
                    <div className="content" key={`non-md-last`}>
                        {processContent(remainingText, note.figures)}
                    </div>
                );
            }
        } else {
            // [markdown]タグがない場合は全体をそのまま処理
            return processContent(note.note.contents, note.figures);
        }
    
        return sections;
    };
    
    return (
        <div className="note-container">
            <div className="note">
                <div className="note-date">
                    <div>作成日: <span className="created-date">{formatDateTimeString(note.note.created_date, 'YYYY年m月d日h:m:s')}</span></div>
                    <div>更新日: <span className="updated-date">{formatDateTimeString(note.note.updated_at, 'YYYY年m月d日h:m:s')}</span></div>
                </div>
                <div className="note-header">
                    <div className="center note-title" contenteditable="true" ref={noteTitleRef}>{note.note.title}</div>
                </div>
                <div className="note-contents" contenteditable="true" ref={noteContentsRef}>
                    {editNoteContents(note)}
                </div>
            </div>
        </div>
    );
};

export default InsightNoteContents;
